<template>
    <component
        :is="to ? 'router-link' : 'div'"
        :to="to"
        :class="['hero-card', 'fade', to ? 'cursor-pointer' : null]"
        :style="cardStyle"
        v-on="$listeners"
    >
        <div
            v-if="image"
            class="hero-card__bg"
            :style="{ backgroundImage: `url(${image})` }"
        />
        <div class="hero-card__bg-overlay" />

        <div class="hero-card__content">
            <div
                v-if="image"
                class="hero-card__media"
                :style="{ backgroundImage: `url(${image})` }"
            />

            <div
                class="hero-card__panel"
                :class="image ? null : 'hero-card__panel--solo'"
            >
                <div class="mb-3">
                    <div
                        v-if="icon"
                        class="hero-card__icon mb-2"
                    >
                        <v-icon
                            color="primary"
                            size="28"
                        >
                            {{ icon }}
                        </v-icon>
                    </div>
                    <h3 class="hero-card__title">
                        <slot name="title">{{ title }}</slot>
                    </h3>
                </div>
                <p class="hero-card__desc">
                    <slot>{{ description }}</slot>
                </p>
                <div class="hero-card__cta eyebrow">
                    {{ cta }}
                    <v-icon
                        color="primary"
                        size="14"
                        class="ml-1"
                    >
                        mdi-arrow-right
                    </v-icon>
                </div>
            </div>
        </div>
    </component>
</template>

<script>
export default {
    name: 'HeroCard',
    props: {
        title: { type: String, default: '' },
        description: { type: String, default: '' },
        icon: { type: String, default: null },
        image: { type: String, default: null },
        to: { type: [String, Object], default: null },
        cta: { type: String, default: 'Acessar' },
        height: { type: [String, Number], default: 480 },
    },
    computed: {
        cardStyle() {
            const h = typeof this.height === 'number' ? `${this.height}px` : this.height
            return { height: h }
        },
    },
}
</script>

<style scoped>
.hero-card {
    position: relative;
    display: block;
    width: 100%;
    border-radius: var(--radius-hero);
    overflow: hidden;
    box-shadow: 0 12px 32px -8px rgba(116, 47, 229, 0.18);
    transition: transform 500ms ease, box-shadow 500ms ease;
    text-decoration: none;
    color: inherit;
}

.hero-card:hover {
    transform: translateY(-12px);
    box-shadow: var(--shadow-glass-hover);
}

.hero-card__bg {
    position: absolute;
    inset: 0;
    background-size: cover;
    background-position: center;
    z-index: 0;
}

.hero-card__bg-overlay {
    position: absolute;
    inset: 0;
    background: rgba(247, 245, 255, 0.55);
    -webkit-backdrop-filter: blur(28px);
    backdrop-filter: blur(28px);
    z-index: 1;
}

.hero-card__content {
    position: relative;
    z-index: 2;
    height: 100%;
    display: flex;
    flex-direction: column;
    padding: 16px;
    gap: 16px;
}

.hero-card__media {
    flex: 0 0 45%;
    border-radius: 32px;
    background-size: cover;
    background-position: center;
    box-shadow: 0 6px 24px -6px rgba(116, 47, 229, 0.25);
    transform: scale(1.05);
    transition: transform 800ms ease;
}

.hero-card:hover .hero-card__media {
    transform: scale(1.02);
}

.hero-card__panel {
    flex: 1;
    background: rgba(247, 245, 255, 0.7);
    -webkit-backdrop-filter: blur(20px);
    backdrop-filter: blur(20px);
    border: 1px solid rgba(178, 140, 255, 0.25);
    border-radius: var(--radius-hero);
    padding: 28px;
    display: flex;
    flex-direction: column;
    justify-content: center;
}

.hero-card__panel--solo {
    flex: 1;
}

.hero-card__icon {
    display: inline-flex;
}

.hero-card__title {
    font-family: var(--font-display);
    font-size: 1.5rem;
    line-height: 1.15;
    font-weight: 800;
    letter-spacing: -0.01em;
    color: var(--color-dark-violet);
    margin: 0;
}

.hero-card__desc {
    font-size: 0.875rem;
    line-height: 1.5;
    font-weight: 500;
    color: rgba(45, 0, 102, 0.78);
    margin: 0 0 24px 0;
}

.hero-card__cta {
    display: inline-flex;
    align-items: center;
    color: var(--color-primary);
    transition: transform 300ms ease;
}

.hero-card:hover .hero-card__cta {
    transform: translateX(8px);
}

/* ===== Dark theme ===== */
html.theme-dark .hero-card__bg-overlay {
    background: rgba(6, 14, 32, 0.55);
}

html.theme-dark .hero-card__panel {
    background: rgba(15, 25, 48, 0.7);
    border-color: rgba(255, 255, 255, 0.08);
}

html.theme-dark .hero-card__desc {
    color: var(--color-on-surface-variant);
}
</style>
